/**
 * SPIN TENET — Run Specification Contract (Gate 2 Foundation)
 *
 * An explicit, versioned, immutable snapshot of the validated CFD physical study definition.
 *
 * SCIENTIFIC IMMUTABILITY RULE:
 * Once created, a RunSpecification is deeply frozen and never changes,
 * even if the user subsequently edits buildings, wind, or domain in the scenario.
 *
 * COORDINATE SYSTEM:
 * Local Cartesian coordinate convention: +X = East, +Z = South, +Y = Up (metres).
 */

import type { BoundaryConditionType, BoundaryConditionsSetup, DomainSetup } from './cfd';
import type { BuildingMassGeometry } from './visualization';
import type { EngineId } from './engine';

export const RUN_SPECIFICATION_VERSION = '1.0.0';
export const RUN_COORDINATE_SYSTEM = 'CARTESIAN_METRES_Y_UP';

export type RunLifecycleStatus =
  | 'DRAFT'
  | 'VALIDATED'
  | 'READY'
  | 'QUEUED'
  | 'PREPARING'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

export interface RunGeometryMassSnapshot {
  id: string;
  type: 'building_mass';
  center: {
    x: number;
    z: number;
  };
  width: number;
  depth: number;
  rotationY: number;
  height: number;
}

export interface RunGeometrySnapshot {
  masses: readonly RunGeometryMassSnapshot[];
  count: number;
}

export interface RunWindSnapshot {
  /** Prescribed speed in m/s (> 0). Must be strictly defined at run creation time. */
  speed: number;
  /** Prescribed direction in degrees [0, 360). Must be strictly defined at run creation time. */
  direction: number;
  /** Reference coordinate convention string. */
  convention: string;
}

export interface RunDomainSnapshot {
  width: number;
  length: number;
  height: number;
  bounds: {
    min: [number, number, number];
    max: [number, number, number];
  };
}

export interface RunBoundaryConditionsSnapshot {
  inlet: BoundaryConditionType;
  outlet: BoundaryConditionType;
  ground: BoundaryConditionType;
  buildings: BoundaryConditionType;
  sides: BoundaryConditionType;
  top: BoundaryConditionType;
}

export interface RunSpecification {
  specificationVersion: string;
  runId: string;
  studyId: string;
  scenarioId: string;
  scenarioName: string;
  createdAt: string;
  engine: EngineId;
  geometry: RunGeometrySnapshot;
  wind: RunWindSnapshot;
  domain: RunDomainSnapshot;
  boundaryConditions: RunBoundaryConditionsSnapshot;
  coordinateSystem: string;
}

/**
 * Validates whether the current scenario setup satisfies all prerequisites to generate a Run Specification.
 */
export function validateRunPrerequisites(params: {
  engine: EngineId | null;
  windSpeed: number | null;
  windDirection: number | null;
  domain: DomainSetup | null;
  geometryMasses: BuildingMassGeometry[];
  boundaryConditions: BoundaryConditionsSetup | null;
}): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!params.engine) {
    errors.push('Cannot create run. Engine selection required.');
  }

  if (params.windSpeed === null || !Number.isFinite(params.windSpeed) || params.windSpeed <= 0) {
    errors.push('Cannot create run. Wind speed is not defined.');
  }

  if (params.windDirection === null || !Number.isFinite(params.windDirection)) {
    errors.push('Cannot create run. Wind direction is not defined.');
  }

  if (!params.domain || params.domain.width <= 0 || params.domain.length <= 0 || params.domain.height <= 0) {
    errors.push('Cannot create run. Domain dimensions must be strictly positive.');
  }

  if (!params.geometryMasses || params.geometryMasses.length === 0) {
    errors.push('Cannot create run. At least one 3D building mass is required.');
  }

  if (!params.boundaryConditions) {
    errors.push('Cannot create run. Boundary conditions must be assigned.');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Factory that captures an immutable snapshot of the study and geometry state.
 * Deep-clones all values and freezes the specification object.
 */
export function createRunSpecificationSnapshot(params: {
  runId: string;
  studyId: string;
  scenarioId: string;
  scenarioName: string;
  engine: EngineId;
  windSpeed: number;
  windDirection: number;
  windConvention: string;
  domain: DomainSetup;
  geometryMasses: BuildingMassGeometry[];
  boundaryConditions: BoundaryConditionsSetup;
}): Readonly<RunSpecification> {
  const halfW = params.domain.width / 2;
  const halfL = params.domain.length / 2;

  const spec: RunSpecification = {
    specificationVersion: RUN_SPECIFICATION_VERSION,
    runId: params.runId,
    studyId: params.studyId,
    scenarioId: params.scenarioId,
    scenarioName: params.scenarioName,
    createdAt: new Date().toISOString(),
    engine: params.engine,
    geometry: {
      count: params.geometryMasses.length,
      masses: params.geometryMasses.map((m) => ({
        id: m.id,
        type: 'building_mass',
        center: { x: Number(m.center.x), z: Number(m.center.z) },
        width: Number(m.width),
        depth: Number(m.depth),
        rotationY: Number(m.rotationY),
        height: Number(m.height)
      }))
    },
    wind: {
      speed: Number(params.windSpeed),
      direction: Number(params.windDirection),
      convention: params.windConvention
    },
    domain: {
      width: Number(params.domain.width),
      length: Number(params.domain.length),
      height: Number(params.domain.height),
      bounds: {
        min: [-halfW, 0, -halfL],
        max: [halfW, params.domain.height, halfL]
      }
    },
    boundaryConditions: {
      inlet: params.boundaryConditions.inlet,
      outlet: params.boundaryConditions.outlet,
      ground: params.boundaryConditions.ground,
      buildings: params.boundaryConditions.buildings,
      sides: params.boundaryConditions.sides,
      top: params.boundaryConditions.top
    },
    coordinateSystem: RUN_COORDINATE_SYSTEM
  };

  // Deep freeze for scientific immutability guarantee
  return Object.freeze({
    ...spec,
    geometry: Object.freeze({
      ...spec.geometry,
      masses: Object.freeze(spec.geometry.masses.map((m) => Object.freeze({ ...m })))
    }),
    wind: Object.freeze({ ...spec.wind }),
    domain: Object.freeze({
      ...spec.domain,
      bounds: Object.freeze({
        min: Object.freeze([...spec.domain.bounds.min]) as [number, number, number],
        max: Object.freeze([...spec.domain.bounds.max]) as [number, number, number]
      })
    }),
    boundaryConditions: Object.freeze({ ...spec.boundaryConditions })
  });
}
